import { Avatar, Group, Tooltip, Anchor, Text } from '@mantine/core';
import { formatDateToShow, formatNumberToCurrency, stringToDate } from 'app/utils/util-functions';
import cleanUUID from 'app/views/components/functions/cleanUUID';
import getInitials from 'app/views/components/functions/getInitials';
import { LoanParams } from 'app/models/loan-params';
import { EntityParams } from 'app/models/entity-params';
import { IndexRateOptionParams } from 'app/models/index-rate-options-params';
import { FacilityParams, IroValues } from 'app/models/facility-params';

interface LoanDetailsProps {
    row: LoanParams;
    entities: EntityParams[];
    allIndexes: IndexRateOptionParams[];
    facility: FacilityParams | undefined;
    iro: IroValues | undefined;
    currentAmountWithInterest?: { amount: number; interest: number };
    navigate: (path: string) => void;
}

export const getLoanDetailsObjectArray = ({
    row,
    entities,
    allIndexes,
    facility,
    iro,
    currentAmountWithInterest,
    navigate,
}: LoanDetailsProps) => {
    const entityIdToName = new Map<string, string>();
    if (Array.isArray(entities)) {
        for (const entity of entities) {
            if (entity.id) {
                entityIdToName.set(entity.id, entity.entityName);
            }
        }
    }

    const hasNonZeroPikRates =
        (Number(row?.pikPremium) || 0) > 0 ||
        (Number(row?.pikInterestPercent) || 0) > 0 ||
        (Number(row?.cashInterestPercent) || 0) > 0;

    const pikConfigDisplay = (() => {
        switch (facility?.pikOption) {
            case 'PIK_All':
                return 'PIK All';
            case 'PIK_Optional':
                return hasNonZeroPikRates ? 'PIK Optional (On)' : 'PIK Optional (Off)';
            case 'PIK_Scheduled':
                return 'PIK Scheduled';
            case 'No_PIK':
            default:
                return 'N/A';
        }
    })();

    // PIK Premium and PIK% / Cash% fields: shown for PIK_Scheduled, and for
    // PIK_Optional only when the loan was created with the PIK toggle on
    // (detected via non-zero rate fields). PIK_All is intentionally excluded
    // since 100%/0% is implied by the PIK Configuration label alone.
    const shouldShowPikDetails =
        (facility?.pikOption === 'PIK_Optional' || facility?.pikOption === 'PIK_Scheduled') &&
        hasNonZeroPikRates;

    const baseFields = [
        {
            label: 'Borrower',
            value: (
                <Group>
                    <Tooltip.Group openDelay={300} closeDelay={100}>
                        <Avatar.Group spacing="sm">
                            {row?.borrower ? (
                                <Anchor key={`row-borrower-${row?.borrower?.id}`}>
                                    <Tooltip
                                        label={entityIdToName.get(cleanUUID(row?.borrower?.id)) || 'Unknown'}
                                        withArrow
                                    >
                                        <Avatar color="violet" radius="xl">
                                            {getInitials(entityIdToName.get(cleanUUID(row?.borrower?.id)) || 'Unknown')}
                                        </Avatar>
                                    </Tooltip>
                                </Anchor>
                            ) : null}
                        </Avatar.Group>
                    </Tooltip.Group>
                </Group>
            ),
        },
        {
            label: 'Amount',
            value: formatNumberToCurrency(Number(row?.amount) ?? 0, row?.currency ?? 'USD'),
        },
        {
            label: 'Start Date',
            value: formatDateToShow(row?.startDate ? stringToDate(row.startDate) : undefined),
        },
        {
            label: 'Maturity Date',
            value: formatDateToShow(row?.endDate ? stringToDate(row.endDate) : undefined),
        },
        {
            label: 'Loan ID',
            value: row?.systemId ?? '',
        },
        {
            label: 'Agent Loan ID',
            value: row?.agentLoanId ?? '',
        },
        {
            label: 'Interest Base Rate with Rounding',
            value: row?.interestBaseRateWithRounding + '',
        },
        {
            label: 'Index Rate Option',
            value: allIndexes.find((index) => index?.id === row?.indexOption?.id)?.indexOption ?? '',
        },
        {
            label: 'Day Basis',
            value: row?.dayBasis ?? '',
        },
        {
            label: 'Interest Due at Current Date',
            value: formatNumberToCurrency(
                currentAmountWithInterest?.interest ?? 0,
                row?.currency ?? 'USD'
            ),
        },
        {
            label: 'CAS',
            value: row?.casValue + '',
        },
        {
            label: 'Interest Base Rate',
            value: row?.interestBaseRate + '',
        },
        {
            label: 'Margin',
            value: row?.margin + '',
        },
    ];

    // Only meaningful for a loan booked in a currency other than the facility's
    // base currency, and only once a rate has actually been set.
    const fxRateField =
        row?.fxRate != null &&
            !!facility?.baseCurrency &&
            row?.currency !== facility.baseCurrency
            ? [{
                label: `FX Rate (${row.currency} → ${facility.baseCurrency})`,
                value: String(row.fxRate),
            }]
            : [];

    const pikConfigurationField = (facility?.pikOption && facility.pikOption !== 'No_PIK') ? [{
        label: 'PIK Configuration',
        value: pikConfigDisplay,
    }] : [];

    const pikPremiumField = shouldShowPikDetails ? [{
        label: 'PIK Premium',
        value: row?.pikPremium?.toString() ?? '0',
    }] : [];

    const allInRateAndPikFields = [
        {
            label: (
                <Text className="sectionItemTitleManual">
                    All-In Rate{' '}
                    {iro?.iroCap === row.allInRate
                        ? '(Cap)'
                        : iro?.iroFloor === row.allInRate
                            ? '(Floor)'
                            : ''}
                </Text>
            ),
            value: (() => {
                // Backward compatibility: use allInRateWithPikPremium (new), fall back to allInRate (old), then 0
                return (Number(row?.allInRateWithPikPremium) || Number(row?.allInRate) || 0).toString();
            })(),
        },
    ];

    // Conditionally add PIK%/Cash% breakdown only for PIK_Optional (option turned on) and PIK_Scheduled
    const pikBreakdownField = shouldShowPikDetails ? [{
        label: 'PIK% / Cash%',
        value: (() => {
            const pikPercent = Number(row?.pikInterestPercent) || 0;
            const cashPercent = Number(row?.cashInterestPercent) || 0;
            return `${pikPercent}% / ${cashPercent}%`;
        })(),
    }] : [];

    const remainingFields = [
        {
            label: (
                <Text
                    className="sectionItemTitleClickable"
                    onClick={() =>
                        navigate(
                            `/dealmanagement/facilities/${facility?.id}/${facility?.status}/${row?.id}/${row?.status}/interest-payment-schedule`
                        )
                    }
                    style={{ cursor: 'pointer' }}
                >
                    Interest Payment Schedule
                </Text>
            ),
            value: formatDateToShow(stringToDate(row?.nextInterestPaymentDate ?? '')),
        },
        {
            label: 'Amount Due',
            value: formatNumberToCurrency(
                (Number(row?.amountDue) || 0) + (Number(row?.pikPremiumInterestAtMaturity) || 0),
                row?.currency ?? 'USD'
            ),
        },
        {
            label: 'Interest Due at Maturity',
            value: formatNumberToCurrency(
                (Number(row?.accrualInterestMaturityDate) || 0) + (Number(row?.pikPremiumInterestAtMaturity) || 0),
                row?.currency ?? 'USD'
            ),
        },
    ];

    return [
        ...baseFields,
        ...fxRateField,
        ...pikConfigurationField,
        ...pikPremiumField,
        ...allInRateAndPikFields,
        ...pikBreakdownField,
        ...remainingFields,
    ];
};
